import { canEdit, deleteExpense } from '../store.js';
import { formatDateIT, formatEur } from '../utils/format.js';
import { escapeHtml } from '../utils/dom.js';
import { authorChip } from '../utils/author.js';
import { categoryLabel } from '../utils/expense-categories.js';
import { computeExpenseAnalytics } from '../utils/expense-analytics.js';
import { consumeExpenseDraft, openExpenseQuickAdd } from '../utils/expense-quick-add.js';
import { getContext } from '../trip-context.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import { bindTripShell, getTripMainEl, renderTripShell } from './shell.js';

const ICON_EDIT = `<svg class="expense-icon-btn__svg" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 20h4l10.5-10.5a2.1 2.1 0 00-3-3L5 17v3z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/><path d="M13.5 6.5l3 3" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>`;

const ICON_TRASH = `<svg class="expense-icon-btn__svg" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M7 7l1 12h8l1-12" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function dayOptionsForTrip(days) {
  return days.map((d) => ({ value: d.date, label: formatDateIT(d.date) }));
}

function defaultDayKey(days) {
  return days[0]?.date ?? '';
}

function openExpenseAddModal(ctx, rootContainer, overrides = {}) {
  if (!canEdit()) return;
  openExpenseQuickAdd({
    instanceId: ctx.instance.id,
    dayKey: overrides.dayKey ?? defaultDayKey(ctx.days),
    dayOptions: dayOptionsForTrip(ctx.days),
    label: overrides.label ?? '',
    amount: overrides.amount ?? null,
    category: overrides.category ?? 'altro',
    costPreview: overrides.costPreview ?? '',
    amountHint: overrides.amountHint ?? '',
    expenseId: overrides.expenseId ?? null,
    onSuccess: () => {
      if (rootContainer) updateExpensesMain(rootContainer, getContext());
    },
  });
}

function groupExpensesByDay(expenses, days) {
  const order = new Map(days.map((d, i) => [d.date, i]));
  const groups = new Map();

  for (const expense of expenses) {
    const list = groups.get(expense.day_key) ?? [];
    list.push(expense);
    groups.set(expense.day_key, list);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => (order.get(a) ?? 999) - (order.get(b) ?? 999))
    .map(([dayKey, items]) => ({
      dayKey,
      items: items.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
      subtotal: items.reduce((sum, e) => sum + Number(e.amount_eur), 0),
    }));
}

function renderBarRow({ label, amount, pct, maxAmount, catClass = '' }) {
  const width = maxAmount > 0 ? Math.max(4, Math.round((amount / maxAmount) * 100)) : 0;
  return `
    <li class="expense-analytics__row">
      <div class="expense-analytics__row-head">
        <span class="expense-analytics__row-label${catClass ? ` expense-cat expense-cat--${escapeHtml(catClass)}` : ''}">${escapeHtml(label)}</span>
        <span class="expense-analytics__row-value">${formatEur(amount)}</span>
      </div>
      <div class="expense-analytics__bar" role="presentation">
        <span class="expense-analytics__bar-fill" style="width: ${width}%"></span>
      </div>
      <span class="expense-analytics__pct">${pct.toFixed(0)}%</span>
    </li>
  `;
}

function renderExpenseAnalytics(ctx) {
  const analytics = computeExpenseAnalytics(ctx.live.expenses, ctx.days);

  if (analytics.count === 0) {
    return `
      <section class="expense-analytics expense-analytics--empty" aria-label="Riepilogo spese">
        <h2 class="expense-analytics__title">Riepilogo</h2>
        <p class="expense-analytics__empty">Nessuna spesa registrata. Aggiungi la prima voce per vedere l’analisi.</p>
      </section>
    `;
  }

  const categoryRows = analytics.categories
    .map((row) =>
      renderBarRow({
        label: row.label,
        amount: row.amount,
        pct: row.pct,
        maxAmount: analytics.maxCategoryAmount,
        catClass: row.id,
      }),
    )
    .join('');

  const dayRows = analytics.daysBreakdown
    .map((row) =>
      renderBarRow({
        label: formatDateIT(row.dayKey),
        amount: row.amount,
        pct: row.pct,
        maxAmount: analytics.maxDayAmount,
      }),
    )
    .join('');

  return `
    <section class="expense-analytics" aria-label="Riepilogo spese">
      <h2 class="expense-analytics__title">Riepilogo</h2>
      <div class="expense-analytics__hero">
        <p class="expense-analytics__hero-label">Totale viaggio</p>
        <p class="expense-analytics__hero-amount">${formatEur(analytics.total)}</p>
        <p class="expense-analytics__hero-meta">
          ${analytics.count} spese · media ${formatEur(analytics.avgPerActiveDay)} / giorno con spese
          · ${formatEur(analytics.avgPerTripDay)} / giorno di viaggio
        </p>
      </div>

      <div class="expense-analytics__block">
        <h3 class="expense-analytics__subtitle">Per categoria</h3>
        <ul class="expense-analytics__list">${categoryRows}</ul>
      </div>

      <div class="expense-analytics__block">
        <h3 class="expense-analytics__subtitle">Per giorno</h3>
        <ul class="expense-analytics__list">${dayRows}</ul>
      </div>
    </section>
  `;
}

function renderExpenseItem(expense, members) {
  const chip = authorChip(members, expense.updated_by, expense.updated_at);
  const cat = expense.category ?? 'altro';

  return `
    <li class="expense-item" data-id="${expense.id}">
      <div class="expense-item__row">
        <button type="button" class="expense-item__body" data-expense-toggle aria-expanded="false">
          <span class="expense-item__main">
            <span class="expense-cat expense-cat--${escapeHtml(cat)}">${escapeHtml(categoryLabel(cat))}</span>
            <span class="expense-item__label">${escapeHtml(expense.label)}</span>
          </span>
          <span class="expense-item__amount">${formatEur(expense.amount_eur)}</span>
        </button>
        ${
          canEdit()
            ? `
          <div class="expense-item__actions">
            <button type="button" class="expense-icon-btn" data-expense-edit data-id="${expense.id}" title="Modifica spesa" aria-label="Modifica spesa">${ICON_EDIT}</button>
            <button type="button" class="expense-icon-btn expense-icon-btn--danger" data-expense-delete data-id="${expense.id}" title="Elimina spesa" aria-label="Elimina spesa">${ICON_TRASH}</button>
          </div>
        `
            : ''
        }
      </div>
      <div class="expense-item__detail" hidden>
        <div class="expense-item__detail-inner">
          ${chip}
        </div>
      </div>
    </li>
  `;
}

function renderExpenseList(ctx) {
  const groups = groupExpensesByDay(ctx.live.expenses, ctx.days);
  if (!groups.length) {
    return '<p class="expense-empty">Nessuna spesa ancora</p>';
  }

  return groups
    .map(
      (group) => `
      <section class="expense-day-group">
        <header class="expense-day-group__head">
          <h3 class="expense-day-group__title">${formatDateIT(group.dayKey)}</h3>
          <span class="expense-day-group__subtotal">${formatEur(group.subtotal)}</span>
        </header>
        <ul class="expense-list">
          ${group.items.map((expense) => renderExpenseItem(expense, ctx.live.members)).join('')}
        </ul>
      </section>
    `,
    )
    .join('');
}

function renderAddTrigger() {
  if (!canEdit()) return '';

  return `
    <div class="expense-add-area" data-expense-add-area>
      <button type="button" class="custom-add-trigger" data-open-expense-add aria-expanded="false">
        <span class="custom-add-trigger__icon" aria-hidden="true">+</span>
        <span class="custom-add-trigger__label">Registra una spesa</span>
      </button>
    </div>
  `;
}

function renderExpensesContent(ctx) {
  const analytics = computeExpenseAnalytics(ctx.live.expenses, ctx.days);

  return `
    ${renderExpenseAnalytics(ctx)}
    <div class="expense-list-wrap">
      <h2 class="expense-list-wrap__title">Dettaglio</h2>
      ${renderExpenseList(ctx)}
    </div>
    ${renderAddTrigger()}
    <div class="expense-total">
      <p class="expense-total__label">Totale viaggio</p>
      <p class="expense-total__amount">${formatEur(analytics.total)}</p>
    </div>
  `;
}

function findExpense(ctx, id) {
  return ctx.live.expenses.find((e) => e.id === id) ?? null;
}

function bindExpenses(main, ctx, rootContainer) {
  const draft = consumeExpenseDraft();
  if (draft && canEdit()) {
    openExpenseAddModal(ctx, rootContainer, draft);
  }

  main.querySelector('[data-open-expense-add]')?.addEventListener('click', () => {
    openExpenseAddModal(ctx, rootContainer);
  });

  main.querySelectorAll('[data-expense-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.expense-item');
      const detail = item?.querySelector('.expense-item__detail');
      if (!detail) return;
      const open = detail.hidden;
      detail.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      item.classList.toggle('expense-item--open', open);
    });
  });

  main.querySelectorAll('[data-expense-edit]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const expense = findExpense(ctx, btn.dataset.id);
      if (!expense) return;
      openExpenseAddModal(ctx, rootContainer, {
        expenseId: expense.id,
        dayKey: expense.day_key,
        label: expense.label,
        amount: Number(expense.amount_eur),
        category: expense.category ?? 'altro',
      });
    });
  });

  main.querySelectorAll('[data-expense-delete]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm('Eliminare questa spesa?')) return;
      try {
        await deleteExpense(btn.dataset.id, ctx.instance.id);
      } catch (error) {
        alert(error.message);
      }
    });
  });
}

function shellOpts(ctx) {
  return {
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
  };
}

export function renderExpenses(container, ctx) {
  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'expenses',
    mainHtml: renderExpensesContent(ctx),
    ...shellOpts(ctx),
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });
  bindExpenses(getTripMainEl(container), ctx, container);
}

export function updateExpensesMain(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;
  main.innerHTML = renderExpensesContent(ctx);
  bindExpenses(main, ctx, container);
}
